import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import inboxNoConversations1 from '../../../../assets/images/inboxNoConversations1.png';
import inboxNoConversations2 from '../../../../assets/images/inboxNoConversations2.png';
import {CellsPanelPageState} from '../../../../store/panels/cells/cellsPanelState';
import {RefinedInboxParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellsRefinementOptionsContainer} from '../../cells/refinement/cellsRefinementOptionsContainer';
import {ClearRefinementButtonContainer} from '../../cells/refinement/clearRefinementButtonContainer';
import {StyledNoConversationsImg, StyledViewerBodyDiv, StyledViewerHeaderDiv} from './emptyInboxesStyles';

/*
 * Props.
 */

interface EmptyRefinedInboxesProps {
  page: CellsPanelPageState;
  parameters: RefinedInboxParameters;
  selectedAssignee: TeammateModel | undefined;
  hasUnassignedFilter: boolean;
}

/*
 * Style.
 */

const StyledViewerDiv = styled.div`
  background-color: ${(p) => p.theme.greys.shade20};
`;

const StyledRefinementOptionsHeaderDiv = styled.div`
  height: 40px;
`;

const StyledRefinementOptionsHeaderRefinementWrapper = styled.div`
  padding-top: 14px;
`;

const StyledContainerDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const StyledContentDiv = styled.div`
  margin-top: -40px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyledEmptyRefinedInboxesHeaderDiv = styled(StyledViewerHeaderDiv)`
  padding: 0;
  max-width: 400px;
  text-align: center;
`;

const StyledEmptyRefinedInboxesBodyDiv = styled(StyledViewerBodyDiv)`
  margin-top: 12px;
`;

const StyledClearRefinementButtonWrapper = styled.div`
  margin-top: 16px;
`;

/*
 * Component.
 */

export const EmptyRefinedInboxes: FC<EmptyRefinedInboxesProps> = (props) => {
  const {page, parameters, selectedAssignee, hasUnassignedFilter} = props;

  const localize = useLocalize();

  if (hasUnassignedFilter && selectedAssignee !== undefined) {
    const noPossibleConversationsHeader = (
      <Trans id="empty-refined-inboxes-has-unassigned-filter-and-assignee-refinement-header">
        No conversations match this filter
      </Trans>
    );

    const noPossibleConversationsBody = (
      <Trans id="empty-refined-inboxes-has-unassigned-filter-and-assignee-refinement-body">
        This view is filtered by an assignee. All open conversations will be found in the
        &ldquo;Assigned&rdquo; tab.
      </Trans>
    );

    return renderEmptyViewer(
      page,
      parameters,
      localize,
      inboxNoConversations1,
      noPossibleConversationsHeader,
      noPossibleConversationsBody,
    );
  }

  const noConversationsHeader = (
    <Trans id="empty-refined-inboxes-header">No conversations match this filter</Trans>
  );

  return renderEmptyViewer(page, parameters, localize, inboxNoConversations2, noConversationsHeader, null);
};

function renderEmptyViewer(
  page: CellsPanelPageState,
  parameters: RefinedInboxParameters,
  localize: Localizer,
  imgSrc: string,
  header: ReactNode,
  body: ReactNode,
) {
  return (
    <StyledViewerDiv>
      <StyledRefinementOptionsHeaderDiv>
        <StyledRefinementOptionsHeaderRefinementWrapper>
          <CellsRefinementOptionsContainer page={page} parameters={parameters} />
        </StyledRefinementOptionsHeaderRefinementWrapper>
      </StyledRefinementOptionsHeaderDiv>
      <StyledContainerDiv>
        <StyledContentDiv>
          <StyledNoConversationsImg
            draggable={false}
            src={imgSrc}
            alt={localize(
              defineMessage({
                id: 'empty-viewer-refined-inboxes',
                message: 'No conversations',
              }),
            )}
          />
          <StyledEmptyRefinedInboxesHeaderDiv>{header}</StyledEmptyRefinedInboxesHeaderDiv>
          {body && <StyledEmptyRefinedInboxesBodyDiv>{body}</StyledEmptyRefinedInboxesBodyDiv>}
          <StyledClearRefinementButtonWrapper>
            <ClearRefinementButtonContainer parameters={parameters} />
          </StyledClearRefinementButtonWrapper>
        </StyledContentDiv>
      </StyledContainerDiv>
    </StyledViewerDiv>
  );
}
